import React, { useState } from "react";
import { X } from "lucide-react";
import {
  AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  FieldGroup, FormField, IconButton, Input, Radio, Textarea,
} from "../../ui";
import { DIFFICULTIES, addTag, removeTag } from "./questionRules";

/**
 * A list of tags plus the field that adds one. The whole box focuses as a unit —
 * `focus-within` puts the ring on the wrapper — so the inner input carries none
 * of its own, which is why it is not an `Input`.
 */
function TagInput({ id, tags, onAdd, onRemove }) {
  const [draft, setDraft] = useState("");
  const commit = () => { if (draft.trim()) onAdd(draft); setDraft(""); };
  return (
    <div className="tw-flex tw-min-h-[38px] tw-flex-wrap tw-items-center tw-gap-1.5 tw-rounded-md tw-border tw-border-solid tw-border-input-line tw-bg-input tw-px-2 tw-py-1.5 focus-within:tw-border-primary focus-within:tw-shadow-focus">
      {tags.map((tag) => (
        <span
          key={tag}
          className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-primary/[0.12] tw-py-0.5 tw-pl-2 tw-pr-1 tw-text-xs tw-font-medium tw-text-primary"
        >
          {tag}
          <IconButton icon={X} label={`Remove ${tag}`} variant="ghost" size="xs" className="tw-h-4 tw-w-4 tw-text-primary" onClick={() => onRemove(tag)} />
        </span>
      ))}
      <input
        id={id}
        className="tw-min-w-[100px] tw-flex-1 tw-border-0 tw-bg-transparent tw-py-0.5 tw-text-[0.8125rem] tw-text-content tw-outline-none placeholder:tw-text-content-subtle"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commit(); }
          if (e.key === "Backspace" && !draft && tags.length) onRemove(tags[tags.length - 1]);
        }}
        onBlur={commit}
        placeholder={tags.length === 0 ? "placement, ielts, campus…" : ""}
      />
    </div>
  );
}

const num = (raw, parse, fallback) => {
  const n = parse(raw);
  return Number.isNaN(n) ? fallback : n;
};

/**
 * Step 5 — how much it is worth and what students read afterwards. Every field
 * here has a default, so an author can move straight on.
 */
export default function MarksStep({ form, set, setForm, errors }) {
  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Scoring</AppCardTitle>
          <AppCardDescription>An assessment can override these marks for its own paper.</AppCardDescription>
        </AppCardHeader>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
          <FieldGroup legend="Difficulty" hint="Random draws in an assessment can ask for easy, medium or hard questions.">
            <div className="tw-grid tw-gap-2 min-[768px]:tw-grid-cols-3">
              {DIFFICULTIES.map((d) => (
                <Radio
                  key={d.value}
                  variant="card"
                  name="question-difficulty"
                  value={d.value}
                  checked={form.difficulty === d.value}
                  onChange={() => set("difficulty", d.value)}
                  label={d.label}
                />
              ))}
            </div>
          </FieldGroup>

          <div className="tw-grid tw-gap-4 min-[768px]:tw-grid-cols-3">
            <FormField label="Marks" error={errors.marks}>
              <Input
                type="number"
                min={0}
                step={0.5}
                value={form.marks}
                onChange={(e) => set("marks", num(e.target.value, Number.parseFloat, 0))}
              />
            </FormField>
            <FormField label="Lost for a wrong answer" hint="0 = no negative marking." error={errors.negativeMarks}>
              <Input
                type="number"
                min={0}
                step={0.25}
                value={form.negativeMarks}
                onChange={(e) => set("negativeMarks", num(e.target.value, Number.parseFloat, 0))}
              />
            </FormField>
            <FormField label="Time to answer" hint="A guide for timing a paper.">
              <Input
                type="number"
                min={5}
                value={form.estimatedTime}
                onChange={(e) => set("estimatedTime", Number.parseInt(e.target.value, 10) || 60)}
                trailing={<span className="tw-text-xs">sec</span>}
                trailingPad="text"
              />
            </FormField>
          </div>
        </AppCardContent>
      </AppCard>

      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Explanation and hint</AppCardTitle>
          <AppCardDescription>
            Optional. The explanation shows when students review their answers; the hint, while they answer.
          </AppCardDescription>
        </AppCardHeader>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
          <FormField label="Explanation" hint="Why the correct answer is right.">
            <Textarea
              rows={3}
              value={form.explanation}
              onChange={(e) => set("explanation", e.target.value)}
              placeholder="e.g. 10% off a price 40% above cost leaves 126% of cost."
            />
          </FormField>
          <FormField label="Hint" hint="A small nudge students can open while answering.">
            <Input value={form.hint} onChange={(e) => set("hint", e.target.value)} placeholder="e.g. Start from a cost of 100." />
          </FormField>
        </AppCardContent>
      </AppCard>

      <AppCard>
        <AppCardHeader>
          <AppCardTitle>Finding it later</AppCardTitle>
          <AppCardDescription>Optional. Tags and the source help you search the bank and build random draws.</AppCardDescription>
        </AppCardHeader>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-flex tw-flex-col tw-gap-1.5">
            <label htmlFor="qe-tag-input" className="tw-text-[0.8125rem] tw-font-semibold tw-text-content">
              Tags <span className="tw-font-normal tw-text-content-muted">— press Enter to add</span>
            </label>
            <TagInput
              id="qe-tag-input"
              tags={form.tags}
              onAdd={(t) => setForm((f) => addTag(f, t))}
              onRemove={(t) => setForm((f) => removeTag(f, t))}
            />
          </div>
          <FormField label="Source">
            <Input value={form.source} onChange={(e) => set("source", e.target.value)} placeholder="e.g. GRE 2024, Aptitude book ch. 3" />
          </FormField>
        </AppCardContent>
      </AppCard>
    </div>
  );
}
